import type { ComponentType, SVGProps } from "react";

import {
  BillsIcon,
  DocumentsIcon,
  EquipmentIcon,
  HomeIcon,
  MoneyIcon,
  PropertiesIcon,
  SettingsIcon,
  TenantsIcon,
  VendorsIcon,
  WorkOrdersIcon,
} from "@/components/ui/icons";

/**
 * The nine top-level destinations EXPERIENCE.md § Information Architecture
 * originally named (nav slots 1–9), plus one real, deliberate tenth — Story
 * 16.2's "Vendors" (spec judgement call 1): the epic's own overview
 * (`epic-16-vendor-directory.md`, Story 16.2 line) already decided this is a
 * genuine new top-level destination, distinct from the still-placeholder
 * "Money" destination below (un-hiding "Money" prematurely would misrepresent
 * the rent ledger as available, which this story does not build) — not
 * something this story re-litigates. This is the one place every
 * destination's order and route are declared — the desktop rail, the phone
 * bottom bar, the "More" sheet and the router table in `main.tsx` all read
 * from it, so a destination cannot exist in one and not another.
 *
 * `phoneSlot` marks the four destinations the phone bottom bar shows
 * directly (DESIGN.md § Layout & Spacing); the remaining five are reachable
 * only through "More".
 *
 * `shortLabel`, owner-approved 2026-08-17 (review round 1): the 60px bottom
 * bar has no room for a mandatory always-visible 16px two-line label on a
 * 16-character destination name — the reviewer measured "Tenants & Leases"
 * needing 70px (icon + two 16px lines) inside the 60px bar, an 11px overlap
 * with zero clearance at the bottom edge, not a styling glitch but an
 * arithmetic mismatch no CSS fix resolves. `shortLabel` exists only for
 * that narrow surface — the desktop rail, the "More" sheet and the
 * destination's own page all keep the full name. Only "Tenants & Leases"
 * needs one; every other bottom-bar/sheet label fits one line at 16px
 * (checked live, including "Bills & Compliance" inside the sheet's wider
 * row).
 *
 * `hidden`, Story 15.14 (Owner-requested 2026-08-25): marks a destination as
 * not yet discoverable through nav — its route, component and this entry
 * all stay real and reachable by direct URL, only the three nav-rendering
 * surfaces (rail, phone bottom bar, "More" sheet) stop listing it. Used for
 * "Bills & Compliance," "Money" and "Work orders" today, all three still
 * pure `DestinationPlaceholder` stubs — flipping this back to `false` (or
 * removing it) once a destination has a real screen is the whole
 * un-hide-when-built path, no re-adding a deleted entry required.
 */
export interface Destination {
  id: string;
  label: string;
  shortLabel?: string;
  path: string;
  sentence: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  phoneSlot: boolean;
  hidden?: boolean;
}

export const DESTINATIONS: readonly Destination[] = [
  {
    id: "home",
    label: "Home",
    path: "/",
    // Story 13.1: `Home` no longer renders `DestinationPlaceholder` at all
    // (this field is now unread for "home" specifically) — kept accurate
    // anyway rather than left stale, since `sentence` is otherwise assumed
    // to describe what the destination actually shows.
    sentence: "Needs attention now and the next 30 days live here.",
    icon: HomeIcon,
    phoneSlot: true,
  },
  {
    id: "properties",
    label: "Properties",
    path: "/properties",
    sentence: "Properties will live here.",
    icon: PropertiesIcon,
    phoneSlot: true,
  },
  {
    id: "tenants-leases",
    label: "Tenants & Leases",
    shortLabel: "Leases",
    path: "/tenants-leases",
    sentence: "Tenants and leases will live here.",
    icon: TenantsIcon,
    phoneSlot: true,
  },
  {
    id: "equipment",
    label: "Equipment",
    path: "/equipment",
    sentence: "Equipment will live here.",
    icon: EquipmentIcon,
    phoneSlot: true,
  },
  {
    id: "bills-compliance",
    label: "Bills & Compliance",
    path: "/bills-compliance",
    sentence: "Bills and compliance items will live here.",
    icon: BillsIcon,
    phoneSlot: false,
    // Story 15.14: still a pure DestinationPlaceholder stub — hidden from
    // every nav surface until it has a real screen behind it.
    hidden: true,
  },
  {
    id: "money",
    label: "Money",
    path: "/money",
    sentence: "The rent ledger, expenses and vendors will live here.",
    icon: MoneyIcon,
    phoneSlot: false,
    // Story 15.14: still a pure DestinationPlaceholder stub — hidden from
    // every nav surface until it has a real screen behind it.
    hidden: true,
  },
  {
    id: "work-orders",
    label: "Work orders",
    path: "/work-orders",
    sentence: "Work orders will live here.",
    icon: WorkOrdersIcon,
    phoneSlot: false,
    // Story 15.14: still a pure DestinationPlaceholder stub — hidden from
    // every nav surface until it has a real screen behind it.
    hidden: true,
  },
  {
    id: "documents",
    label: "Documents",
    path: "/documents",
    sentence: "Documents will live here.",
    icon: DocumentsIcon,
    phoneSlot: false,
  },
  {
    // Story 16.2 — the genuine tenth destination (module doc comment above).
    // `phoneSlot: false` (judgement call 2): the phone bottom bar already
    // carries its own four real, previously-reasoned-about slots (Home,
    // Properties, Tenants & Leases, Equipment — DESIGN.md § Layout &
    // Spacing) and a vendor lookup is real but lower-frequency than any of
    // those four (an Owner calls a vendor occasionally, not on every visit
    // the way they check what needs attention or look up a tenant); reached
    // through "More" instead, one extra tap, the same place Documents and
    // Settings already sit.
    id: "vendors",
    label: "Vendors",
    path: "/vendors",
    // `VendorsList.tsx` renders from the very first commit of this
    // destination — never `DestinationPlaceholder` — so this field is
    // unread for "vendors" specifically, the identical shape `Home`'s own
    // doc comment above already established. Kept accurate anyway rather
    // than left stale (that comment's own rule).
    sentence: "Every vendor you call for repairs and maintenance lives here.",
    icon: VendorsIcon,
    phoneSlot: false,
  },
  {
    id: "settings",
    label: "Settings",
    path: "/settings",
    sentence: "Account, users and preferences will live here.",
    icon: SettingsIcon,
    phoneSlot: false,
    // Story 20.1: a dedicated gear-icon button on both top bars now
    // navigates straight to /settings (the same "icon-only utility
    // control" class as search/AlertBell/account), so a second path to the
    // same place through the Menu panel would be redundant — the exact
    // Story 15.14 `hidden` mechanism above, reused rather than reinvented.
    // The route, page title and RouteAnnouncer behavior all stay real
    // (this file's own comment above: they read DESTINATIONS, not
    // VISIBLE_DESTINATIONS).
    hidden: true,
  },
] as const;

// The three nav-rendering surfaces (rail, phone bottom bar, "More" sheet)
// all read from this, never from raw `DESTINATIONS` — everything else
// (the router table, `RouteAnnouncer`, `DestinationPlaceholder`/`Settings`
// looking themselves up by id or path) keeps reading `DESTINATIONS`
// directly, since a hidden destination's route, title and announcement all
// stay real, only its nav discoverability goes away (Story 15.14).
export const VISIBLE_DESTINATIONS = DESTINATIONS.filter((d) => !d.hidden);

export const PHONE_BAR_DESTINATIONS = VISIBLE_DESTINATIONS.filter((d) => d.phoneSlot);
export const MORE_SHEET_DESTINATIONS = VISIBLE_DESTINATIONS.filter((d) => !d.phoneSlot);
